/**
 * FormEditorPage — dedicated full-page editor for clinic forms.
 *
 * Routes:
 *   /dashboard/forms/new        → create
 *   /dashboard/forms/:formId    → edit (cloud form) or view (system form)
 *
 * Features: Google-Docs-style editor (FormDocumentEditor), explicit save +
 * Ctrl/Cmd+S, unsaved-changes guard, crash-safe local draft, preview mode,
 * duplicate system forms into an editable copy, hand-off to "send for
 * signature" in the toolbox.
 *
 * Error codes: CK-6020..CK-6029
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ArrowRight,
  FloppyDisk,
  Eye,
  PencilSimple,
  PaperPlaneTilt,
  Copy,
  WarningCircle,
  Lock,
  FileText,
  ClockCounterClockwise,
  X,
} from "@phosphor-icons/react";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectIsAdmin,
  selectClinicProfile,
} from "../../store/slices/authSlice";
import {
  useGetFormQuery,
  useCreateFormMutation,
  useUpdateFormMutation,
} from "../../store/services/formsApi";
import { getSystemForms } from "../../data/systemContent";
import { useToast } from "../../contexts/ToastContext";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { HelpButton } from "../../components/shared";
import ContentLoader from "../../components/shared/ContentLoader";
import LoaderLoop from "../../components/shared/loader/LoaderLoop";
import SystemContentDisclaimer from "../../components/shared/SystemContentDisclaimer";
import FormDocumentEditor from "../../components/forms/editor/FormDocumentEditor";
import { countSignatureSlots } from "../../components/forms/formPlaceholders";
import "../../styles/formDocument.css";
import "./FormEditorPage.css";

const DRAFT_PREFIX = "ck_form_draft_";
const EMPTY_DOC = "<p></p>";

const isSystemFormId = (id) => typeof id === "string" && id.startsWith("sys_form_");

const readDraft = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const writeDraft = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify({ ...value, savedAt: Date.now() }));
  } catch (_) {
    /* quota / private mode — ignore */
  }
};

const clearDraft = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch (_) {
    /* ignore */
  }
};

const normalizeHtml = (html) => (html || "").trim();
const isEmptyHtml = (html) => {
  const stripped = normalizeHtml(html)
    .replace(/<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>/g, "")
    .replace(/\s/g, "");
  return stripped === "";
};

function FormEditorPage() {
  const { formId } = useParams();
  const isNew = !formId || formId === "new";
  const isSystem = isSystemFormId(formId);
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const isAdmin = useSelector(selectIsAdmin);
  const clinicProfile = useSelector(selectClinicProfile);

  const {
    data: cloudForm,
    isLoading: isLoadingForm,
    isError: isFormError,
  } = useGetFormQuery({ clinicId, formId }, { skip: !clinicId || isNew || isSystem });

  const systemForm = useMemo(() => {
    if (!isSystem) return null;
    return getSystemForms(clinicProfile?.type || "").find((f) => f.id === formId) || null;
  }, [isSystem, formId, clinicProfile?.type]);

  const [createForm, { isLoading: isCreating }] = useCreateFormMutation();
  const [updateForm, { isLoading: isUpdating }] = useUpdateFormMutation();
  const isSaving = isCreating || isUpdating;

  // ---------- Local document state ----------
  const [name, setName] = useState("");
  const [content, setContent] = useState(EMPTY_DOC);
  const [savedSnapshot, setSavedSnapshot] = useState({ name: "", content: EMPTY_DOC });
  const [loaded, setLoaded] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [previewMode, setPreviewMode] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false });
  const nameInputRef = useRef(null);

  const draftKey = `${DRAFT_PREFIX}${clinicId || "x"}_${isNew ? "new" : formId}`;

  const sourceForm = isSystem ? systemForm : cloudForm;
  const canEdit = !isSystem && (isNew || isAdmin || (sourceForm && sourceForm.createdBy === therapistId));
  const readOnly = !canEdit;

  // Load the source form into local state once
  useEffect(() => {
    if (loaded) return;
    if (isNew) {
      const draft = readDraft(draftKey);
      setName("");
      setContent(EMPTY_DOC);
      setSavedSnapshot({ name: "", content: EMPTY_DOC });
      setLoaded(true);
      if (draft && (draft.name || !isEmptyHtml(draft.content))) setPendingDraft(draft);
      setTimeout(() => nameInputRef.current?.focus(), 50);
      return;
    }
    if (!sourceForm) return;
    const initial = {
      name: sourceForm.name || "",
      content: normalizeHtml(sourceForm.content) || EMPTY_DOC,
    };
    setName(initial.name);
    setContent(initial.content);
    setSavedSnapshot(initial);
    setLoaded(true);
    const draft = readDraft(draftKey);
    if (
      draft &&
      !isSystem &&
      (draft.name !== initial.name || normalizeHtml(draft.content) !== normalizeHtml(initial.content))
    ) {
      setPendingDraft(draft);
    }
  }, [loaded, isNew, sourceForm, draftKey, isSystem]);

  const isDirty =
    loaded &&
    canEdit &&
    (name.trim() !== savedSnapshot.name.trim() ||
      normalizeHtml(content) !== normalizeHtml(savedSnapshot.content));

  // Crash-safe draft: persist while dirty, clear when clean
  useEffect(() => {
    if (!loaded || !canEdit) return undefined;
    if (!isDirty) {
      clearDraft(draftKey);
      return undefined;
    }
    const timer = setTimeout(() => writeDraft(draftKey, { name, content }), 700);
    return () => clearTimeout(timer);
  }, [name, content, isDirty, loaded, canEdit, draftKey]);

  // Warn on tab close with unsaved changes
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // ---------- Save ----------
  const handleSave = useCallback(async () => {
    if (!canEdit || isSaving) return null;
    const trimmedName = name.trim();
    if (!trimmedName) {
      showError("יש להזין שם לטופס");
      nameInputRef.current?.focus();
      return null;
    }
    if (isEmptyHtml(content)) {
      showError("תוכן הטופס ריק");
      return null;
    }
    const payload = { name: trimmedName, content };
    try {
      if (isNew) {
        const created = await createForm({
          clinicId,
          formData: payload,
          creatorId: therapistId,
          creatorName: therapistName,
        }).unwrap();
        clearDraft(draftKey);
        setSavedSnapshot(payload);
        showSuccess("הטופס נוצר בהצלחה");
        navigate(`/dashboard/forms/${created.id}`, { replace: true });
        return created.id;
      }
      await updateForm({
        clinicId,
        formId,
        formData: payload,
        editorId: therapistId,
        editorName: therapistName,
      }).unwrap();
      clearDraft(draftKey);
      setSavedSnapshot(payload);
      showSuccess("הטופס נשמר");
      return formId;
    } catch (error) {
      console.error("[CK-6020] Error saving form from editor page:", {
        errorCode: "CK-6020",
        errorMessage: error?.message || String(error),
        formId: isNew ? "new" : formId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת הטופס");
      return null;
    }
  }, [
    canEdit, isSaving, name, content, isNew, createForm, updateForm, clinicId, formId,
    therapistId, therapistName, draftKey, navigate, showSuccess, showError,
  ]);

  // Ctrl/Cmd + S
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (isDirty) handleSave();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleSave, isDirty]);

  // ---------- Navigation guard ----------
  const goBack = useCallback(() => navigate("/dashboard/tasks?tab=forms"), [navigate]);

  const handleBack = () => {
    if (!isDirty) {
      goBack();
      return;
    }
    setConfirmDialog({
      isOpen: true,
      type: "warning",
      icon: "warning",
      title: "שינויים שלא נשמרו",
      message: "יש בטופס שינויים שטרם נשמרו. לשמור לפני היציאה?",
      confirmText: "שמור וצא",
      cancelText: "המשך לערוך",
      extraAction: { label: "צא בלי לשמור", onClick: () => { clearDraft(draftKey); goBack(); } },
      onConfirm: async () => {
        const id = await handleSave();
        if (id) goBack();
      },
    });
  };

  // ---------- Send for signature ----------
  const handleSend = async () => {
    let id = isNew ? null : formId;
    if (isDirty || isNew) {
      id = await handleSave();
      if (!id) return;
    }
    navigate(`/dashboard/tasks?tab=forms&sendForm=${id}`);
  };

  // ---------- Duplicate (system / not-owned form) ----------
  const handleDuplicate = async () => {
    if (!sourceForm) return;
    try {
      const created = await createForm({
        clinicId,
        formData: {
          name: `${sourceForm.name} (העתק)`,
          content: sourceForm.content || "",
          ...(isSystem ? { duplicatedFromSystemId: sourceForm.id } : {}),
        },
        creatorId: therapistId,
        creatorName: therapistName,
      }).unwrap();
      showSuccess("הטופס שוכפל — כעת ניתן לערוך אותו");
      setLoaded(false);
      setPreviewMode(false);
      navigate(`/dashboard/forms/${created.id}`, { replace: true });
    } catch (error) {
      console.error("[CK-6021] Error duplicating form from editor page:", {
        errorCode: "CK-6021",
        errorMessage: error?.message || String(error),
        formId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשכפול הטופס");
    }
  };

  // Reset local state when route changes (e.g. after create → edit redirect)
  const lastFormIdRef = useRef(formId);
  useEffect(() => {
    if (lastFormIdRef.current !== formId) {
      lastFormIdRef.current = formId;
      // After creating a new form we already hold the latest content; just
      // rebind the snapshot to the new id without reloading the editor.
      if (!isNew && normalizeHtml(content) === normalizeHtml(savedSnapshot.content) && name.trim() === savedSnapshot.name.trim()) {
        return;
      }
      setLoaded(false);
      setResetKey((k) => k + 1);
    }
  }, [formId, isNew, content, name, savedSnapshot]);

  // ---------- Draft restore ----------
  const restoreDraft = () => {
    if (!pendingDraft) return;
    setName(pendingDraft.name || "");
    setContent(normalizeHtml(pendingDraft.content) || EMPTY_DOC);
    setResetKey((k) => k + 1);
    setPendingDraft(null);
  };
  const discardDraft = () => {
    clearDraft(draftKey);
    setPendingDraft(null);
  };

  // ---------- Derived ----------
  const signatureCount = useMemo(() => countSignatureSlots(content), [content]);
  // The editor reads `value` only on mount, so never render it before the
  // source form has been copied into local state (`loaded`).
  const notFound =
    !isNew &&
    !loaded &&
    !!clinicId &&
    !isLoadingForm &&
    (isSystem ? !!clinicProfile && !systemForm : isFormError || (!cloudForm && !isLoadingForm));
  const showLoading = !isNew && !loaded && !notFound;

  const statusLabel = !canEdit
    ? "צפייה בלבד"
    : isSaving
    ? "שומר…"
    : isDirty
    ? "שינויים שלא נשמרו"
    : isNew
    ? "טופס חדש"
    : "כל השינויים נשמרו";

  if (showLoading) {
    return (
      <div className="form-editor-page">
        <ContentLoader fill message="טוען טופס…" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="form-editor-page">
        <div className="fep-loading">
          <WarningCircle size={32} weight="duotone" />
          <span>הטופס לא נמצא</span>
          <button type="button" className="fep-btn fep-btn--secondary" onClick={goBack}>
            חזרה לארגז הכלים
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="form-editor-page">
      <header className="fep-header">
        <div className="fep-header-start">
          <button type="button" className="fep-icon-btn" onClick={handleBack} title="חזרה לארגז הכלים" aria-label="חזרה">
            <ArrowRight size={20} weight="bold" />
          </button>
          <div className="fep-title-wrap">
            <FileText size={20} weight="duotone" className="fep-title-icon" />
            <input
              ref={nameInputRef}
              type="text"
              className="fep-title-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="שם הטופס (לדוגמא: הסכמה לטיפול)"
              disabled={readOnly}
              aria-label="שם הטופס"
              maxLength={120}
            />
          </div>
        </div>

        <div className="fep-header-end">
          <span className={`fep-status${isDirty ? " is-dirty" : ""}${readOnly ? " is-readonly" : ""}`}>
            {readOnly && <Lock size={13} weight="bold" />}
            {statusLabel}
          </span>
          <HelpButton featureKey="form_modal" />
          <button
            type="button"
            className={`fep-btn fep-btn--ghost${previewMode ? " is-active" : ""}`}
            onClick={() => setPreviewMode((v) => !v)}
            title={previewMode ? "חזרה לעריכה" : "תצוגה מקדימה"}
          >
            {previewMode ? <PencilSimple size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
            <span>{previewMode ? "עריכה" : "תצוגה מקדימה"}</span>
          </button>
          {canEdit ? (
            <>
              <button
                type="button"
                className="fep-btn fep-btn--secondary"
                onClick={handleSend}
                disabled={isSaving}
                title={signatureCount === 0 ? "מומלץ להוסיף מקום לחתימת לקוח לפני השליחה" : "שמור ושלח לחתימה"}
              >
                <PaperPlaneTilt size={16} weight="bold" />
                <span>שלח לחתימה</span>
              </button>
              <button
                type="button"
                className="fep-btn fep-btn--primary"
                onClick={handleSave}
                disabled={isSaving || (!isDirty && !isNew)}
              >
                {isSaving ? <LoaderLoop size="xs" /> : <FloppyDisk size={16} weight="bold" />}
                <span>{isNew ? "צור טופס" : "שמור"}</span>
              </button>
            </>
          ) : (
            <button type="button" className="fep-btn fep-btn--primary" onClick={handleDuplicate} disabled={isCreating}>
              {isCreating ? <LoaderLoop size="xs" /> : <Copy size={16} weight="bold" />}
              <span>שכפל לעריכה</span>
            </button>
          )}
        </div>
      </header>

      {pendingDraft && (
        <div className="fep-banner" role="status">
          <ClockCounterClockwise size={18} weight="bold" />
          <span>נמצאה טיוטה שלא נשמרה מעריכה קודמת.</span>
          <button type="button" className="fep-btn fep-btn--secondary fep-btn--sm" onClick={restoreDraft}>
            שחזר טיוטה
          </button>
          <button type="button" className="fep-icon-btn fep-icon-btn--sm" onClick={discardDraft} aria-label="התעלם מהטיוטה">
            <X size={16} weight="bold" />
          </button>
        </div>
      )}

      {readOnly && !pendingDraft && (
        <div className="fep-banner fep-banner--info" role="status">
          <Lock size={16} weight="bold" />
          <span>
            {isSystem
              ? "זהו טופס מובנה של CliniKit. שכפלו אותו כדי ליצור עותק אישי שניתן לערוך."
              : "טופס זה נוצר על ידי מטפל/ת אחר/ת. ניתן לצפות בו או לשכפל אותו לעריכה."}
          </span>
        </div>
      )}

      {isSystem && <SystemContentDisclaimer kind="form" compact />}

      {signatureCount === 0 && canEdit && !previewMode && (
        <div className="fep-banner fep-banner--tip" role="status">
          <WarningCircle size={16} weight="bold" />
          <span>טיפ: הוסיפו "חתימת לקוח" מהתפריט הצדדי במקום שבו המטופל צריך לחתום.</span>
        </div>
      )}

      <div className="fep-body">
        {previewMode ? (
          <div className="fep-preview">
            <div className="fep-preview-paper">
              <h1 className="fep-preview-title">{name.trim() || "טופס ללא שם"}</h1>
              <div className="ck-form-doc" dangerouslySetInnerHTML={{ __html: content }} />
            </div>
          </div>
        ) : (
          <FormDocumentEditor
            key={`${isNew ? "new" : formId}-${resetKey}`}
            value={content}
            onChange={setContent}
            readOnly={readOnly}
            resetKey={resetKey}
          />
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false })}
        onConfirm={async () => {
          const fn = confirmDialog.onConfirm;
          setConfirmDialog({ isOpen: false });
          if (fn) await fn();
        }}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        type={confirmDialog.type}
        icon={confirmDialog.icon}
      >
        {confirmDialog.extraAction && (
          <button
            type="button"
            className="fep-btn fep-btn--danger-ghost"
            onClick={() => {
              const fn = confirmDialog.extraAction.onClick;
              setConfirmDialog({ isOpen: false });
              fn();
            }}
          >
            {confirmDialog.extraAction.label}
          </button>
        )}
      </ConfirmDialog>
    </div>
  );
}

export default FormEditorPage;
